import { RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { addDays } from 'date-fns'

import DateRange, {
  isInclusivelyAfterDay,
  SelectorDate,
  TooltipProps as DateRangeTooltipProps,
} from 'common/components/dateRange'
import FormItem from 'common/components/formItem'

type Props = DateRangeTooltipProps & {
  applyDates: (startDate?: string, endDate?: string) => void
  selectedStartDate?: string
  selectedEndDate?: string
  isDisabled?: boolean
  areDefaultOptionsDisabled?: boolean
  flexWrapperRef?: RefObject<Element> | null
  label?: string
}

const DateRangePicker = ({
  selectedStartDate,
  selectedEndDate,
  applyDates,
  isDisabled = false,
  label = 'Date range',
  flexWrapperRef,
  areDefaultOptionsDisabled = false,
  ...rest
}: Props) => {
  const [startDate, setStartDate] = useState<SelectorDate>(null)
  const [endDate, setEndDate] = useState<SelectorDate>(null)

  useEffect(() => {
    setStartDate(selectedStartDate ? new Date(selectedStartDate) : null)
    setEndDate(selectedEndDate ? new Date(selectedEndDate) : null)
  }, [selectedStartDate, selectedEndDate])

  const hasDateChanged =
    startDate?.toISOString() !== selectedStartDate ||
    endDate?.toISOString() !== selectedEndDate
  const areBothDatesSelected =
    (startDate && endDate) || (!startDate && !endDate)

  const ref = useRef<HTMLDivElement>(null)
  const [isOnFirstRow, setIsOnFirstRow] = useState(
    ref.current ? ref.current.offsetTop === 0 : undefined
  )
  if (isOnFirstRow === undefined && ref.current) {
    setIsOnFirstRow(ref.current.offsetTop === 0)
  }

  useLayoutEffect(() => {
    const observer = new ResizeObserver(() => {
      if (ref.current) {
        setIsOnFirstRow(ref.current.offsetTop === 0)
      }
    })
    const container = flexWrapperRef?.current
    if (container) {
      observer.observe(container)
    }
    return () => {
      if (container) {
        observer.unobserve(container)
      }
    }
  })

  return (
    <div ref={ref}>
      <FormItem label={label}>
        <DateRange
          key={`${selectedStartDate}-${selectedEndDate}`}
          startDate={startDate}
          endDate={endDate}
          onDatesChange={({ startDate: newStartDate, endDate: newEndDate }) => {
            if (newStartDate !== startDate) {
              setStartDate(newStartDate)
              setEndDate(null)
            } else {
              setStartDate(newStartDate)
              setEndDate(newEndDate)
            }
          }}
          isOutsideRange={(day) =>
            isInclusivelyAfterDay(day, addDays(new Date(), 1))
          }
          disabled={isDisabled}
          showClearDates
          maxDate={new Date()}
          onOutsideClick={() => {
            setStartDate(selectedStartDate ? new Date(selectedStartDate) : null)
            setEndDate(selectedEndDate ? new Date(selectedEndDate) : null)
          }}
          footer={{
            onCancel: () => {
              setStartDate(
                selectedStartDate ? new Date(selectedStartDate) : null
              )
              setEndDate(selectedEndDate ? new Date(selectedEndDate) : null)
            },
            onApply: () => {
              applyDates(startDate?.toISOString(), endDate?.toISOString())
            },
            isApplyDisabled: !areBothDatesSelected || !hasDateChanged,
            hasDateChanged,
            areDefaultOptionsDisabled: areDefaultOptionsDisabled,
            applyDefaultOption: (start, end) => {
              setStartDate(start)
              setEndDate(end)
            },
          }}
          {...rest}
        />
      </FormItem>
    </div>
  )
}

export default DateRangePicker
